import React, { useState, useEffect } from 'react';
import { ApiKey, ApiKeyDetails } from '../types';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import Toast from '../components/ui/Toast';
import { Key, Plus, Trash2, Copy, AlertCircle, ChevronDown } from 'lucide-react';
import { api } from '../services/api';
import { useSubscriptionError } from '../context/SubscriptionErrorContext';
import { handleApiError } from '../utils/errorHandling';
import { useAuth } from '../hooks/useAuth';

interface CreateKeyResponse {
    apiKey: {
      token: string;
      apiKeyId: string;
      type?: 'integration' | 'sdk';
      userId?: string;
      createdBy?: string;
    }
}

interface GetKeysResponse {
    apiKeys: ApiKeyDetails[];
}

const ApiKeysPage: React.FC = () => {
  const [keys, setKeys] = useState<ApiKeyDetails[]>([]);
  const [newKeyName, setNewKeyName] = useState('');
  const [newKeyDescription, setNewKeyDescription] = useState('');
  const [newKeyType, setNewKeyType] = useState<'integration' | 'sdk'>('integration');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [newlyCreatedKey, setNewlyCreatedKey] = useState<ApiKey | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deletingKeyId, setDeletingKeyId] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [keyToDelete, setKeyToDelete] = useState<ApiKeyDetails | null>(null);
  const [typeDropdownOpen, setTypeDropdownOpen] = useState(false);
  const [showCopiedToast, setShowCopiedToast] = useState(false);
  const { setSubscriptionError } = useSubscriptionError();
  const { user } = useAuth();

  const fetchKeys = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get<GetKeysResponse>('/api/api-keys');
      setKeys(response.apiKeys || []);
      setSubscriptionError(null); // Clear any previous subscription error
    } catch (err: any) {
      handleApiError(err, setError, setSubscriptionError);
      setKeys([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchKeys();
  }, []);

  const handleCreateKey = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newKeyName.trim()) return;

    setIsCreating(true);
    try {
        const requestBody: any = { 
          name: newKeyName, 
          description: newKeyDescription,
          type: newKeyType
        };
        
        // Add userId only for integration keys
        if (newKeyType === 'integration' && user?.userId) {
          requestBody.userId = user.userId;
        }

        const response = await api.post<CreateKeyResponse>('/api/api-keys', requestBody);
        const { apiKey } = response;

        const fullNewKey: ApiKey = {
            ...apiKey,
            name: newKeyName,
            description: newKeyDescription,
            createdAt: new Date().toISOString(),
            type: newKeyType,
        };
        setNewlyCreatedKey(fullNewKey);
        
        setShowCreateForm(false);
        setNewKeyName('');
        setNewKeyDescription('');
        setNewKeyType('integration'); // Reset to default
        await fetchKeys();

    } catch (err: any) {
        handleApiError(err, (errorMsg) => alert(`Failed to create key: ${errorMsg}`), setSubscriptionError);
    } finally {
      setIsCreating(false);
    }
  };

  const handleDeleteClick = (key: ApiKeyDetails) => {
    setKeyToDelete(key);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    if (!keyToDelete) return;
    
    setDeletingKeyId(keyToDelete.apiKeyId);
    try {
      await api.delete(`/api/api-keys/${keyToDelete.apiKeyId}`);
      setKeys(prevKeys => prevKeys.filter(key => key.apiKeyId !== keyToDelete.apiKeyId));
      setSubscriptionError(null); // Clear subscription error on successful deletion
    } catch (err: any) {
      handleApiError(err, (errorMsg) => alert(`Failed to delete key: ${errorMsg}`), setSubscriptionError);
    } finally {
      setDeletingKeyId(null);
      setShowDeleteConfirm(false);
      setKeyToDelete(null);
    }
  };
  
  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setShowCopiedToast(true);
  };

  const getKeyTypeBadge = (type?: 'integration' | 'sdk') => {
    // Default to 'sdk' if type is undefined (legacy keys)
    const keyType = type || 'sdk';
    
    if (keyType === 'integration') {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium dark:bg-blue-900/30 bg-blue-100 dark:text-blue-400 text-blue-700 border dark:border-blue-700 border-blue-300">
          Integration
        </span>
      );
    }
    
    return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium dark:bg-purple-900/30 bg-purple-100 dark:text-purple-400 text-purple-700 border dark:border-purple-700 border-purple-300">
        SDK
      </span>
    );
  };

  return (
    <div className="space-y-4 sm:space-y-8">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold dark:text-text-primary text-light-text-primary">API Keys</h1>
        <p className="text-sm sm:text-base dark:text-text-secondary text-light-text-secondary">Manage API keys for accessing the Mentoros API.</p>
      </div>

      {newlyCreatedKey && (
        <Card title={`New ${newlyCreatedKey.type === 'integration' ? 'Integration' : 'SDK'} API Key Created`} className="border-green-500">
          <div className="mb-3">
            {getKeyTypeBadge(newlyCreatedKey.type)}
          </div>
          <p className="text-sm dark:text-text-secondary text-light-text-secondary mb-2">Please copy your new API key. You will not be able to see it again.</p>
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 dark:bg-background bg-light-background p-2 rounded-md">
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <Key className="w-5 h-5 dark:text-text-secondary text-light-text-secondary flex-shrink-0" />
              <code className="dark:text-text-primary text-light-text-primary text-xs sm:text-sm break-all">{newlyCreatedKey.token}</code>
            </div>
            <Button variant="secondary" onClick={() => copyToClipboard(newlyCreatedKey.token)} className="w-full sm:w-auto flex-shrink-0">
              <Copy className="w-4 h-4 mr-2"/> Copy
            </Button>
          </div>
           <div className="mt-4">
             <Button onClick={() => setNewlyCreatedKey(null)}>Done</Button>
           </div>
        </Card>
      )}

      <Card
        title="Your API Keys"
        actions={
          <Button onClick={() => setShowCreateForm(!showCreateForm)} disabled={showCreateForm}>
            <Plus className="w-4 h-4 sm:mr-2"/> <span className="hidden sm:inline">Create New Key</span>
          </Button>
        }
      >
        {showCreateForm && (
          <form onSubmit={handleCreateKey} className="p-4 border-b dark:border-border-color border-light-border-color mb-4 space-y-4">
            <h3 className="font-semibold dark:text-text-primary text-light-text-primary">New API Key</h3>
            
            {/* Type Selector */}
            <div className="space-y-2">
              <label className="block text-sm font-medium dark:text-text-primary text-light-text-primary">
                Key Type
              </label>
              <div className="relative">
                <select
                  value={newKeyType}
                  onChange={(e) => {
                    setNewKeyType(e.target.value as 'integration' | 'sdk');
                    setTypeDropdownOpen(false);
                  }}
                  onMouseDown={() => setTypeDropdownOpen(!typeDropdownOpen)}
                  onBlur={() => setTypeDropdownOpen(false)}
                  className="appearance-none w-full px-4 py-2 dark:bg-background bg-light-background dark:text-text-primary text-light-text-primary border dark:border-border-color border-light-border-color rounded-md focus:outline-none focus:ring-2 focus:ring-primary cursor-pointer"
                >
                  <option value="integration">Integration - For external systems (binds to your user)</option>
                  <option value="sdk">SDK - For hosted chatbot SDK</option>
                </select>
                <ChevronDown 
                  className={`absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 dark:text-text-secondary text-light-text-secondary pointer-events-none transition-transform ${
                    typeDropdownOpen ? 'rotate-180' : ''
                  }`}
                />
              </div>
              <p className="text-xs dark:text-text-secondary text-light-text-secondary">
                {newKeyType === 'integration' 
                  ? 'Integration keys are used for API calls from external systems and bind to your user account.'
                  : 'SDK keys are used for the hosted chatbot widget and do not require user binding.'}
              </p>
            </div>

            <Input
              label="Key Name"
              placeholder="e.g., My Web App"
              value={newKeyName}
              onChange={(e) => setNewKeyName(e.target.value)}
              required
            />
            <Input
              label="Description (optional)"
              placeholder="e.g., Used for the main production server"
              value={newKeyDescription}
              onChange={(e) => setNewKeyDescription(e.target.value)}
            />
            <div className="flex flex-col sm:flex-row gap-2 justify-end">
              <Button variant="secondary" onClick={() => {
                setShowCreateForm(false);
                setNewKeyType('integration'); // Reset to default
              }} type="button" className="w-full sm:w-auto">Cancel</Button>
              <Button type="submit" isLoading={isCreating} className="w-full sm:w-auto">Create Key</Button>
            </div>
          </form>
        )}
        
        {loading ? (
          <div className="flex justify-center items-center p-8">
            <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : error ? (
            <div className="flex flex-col items-center justify-center p-8 text-red-500">
                <AlertCircle className="w-8 h-8 mb-2" />
                <p>{error}</p>
                <Button onClick={fetchKeys} className="mt-4" variant="secondary">Retry</Button>
            </div>
        ) : (
          <div className="space-y-4">
              {keys.length > 0 ? keys.map(key => (
                <div key={key.apiKeyId} className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-3 dark:bg-background bg-light-background rounded-md">
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2 mb-1">
                      <p className="font-semibold dark:text-text-primary text-light-text-primary break-words">{key.name}</p>
                      {getKeyTypeBadge(key.type)}
                    </div>
                    {key.description && (
                      <p className="text-sm dark:text-text-secondary text-light-text-secondary mt-1 break-words">{key.description}</p>
                    )}
                    <div className="flex items-center gap-2 mt-2 dark:bg-gray-800/50 bg-gray-100 px-3 py-1.5 rounded border dark:border-gray-700 border-gray-300 w-full sm:w-fit overflow-x-auto">
                      <Key className="w-3.5 h-3.5 dark:text-gray-400 text-gray-600 flex-shrink-0" />
                      <code className="text-xs sm:text-sm dark:text-gray-300 text-gray-700 font-mono">{key.tokenMasked}</code>
                    </div>
                    <p className="text-xs dark:text-gray-500 text-gray-600 mt-2">Created on {new Date(key.createdAt).toLocaleDateString()}</p>
                  </div>
                  <div className="flex items-center gap-2 sm:flex-col">
                    <Button
                      variant="danger"
                      size="icon"
                      onClick={() => handleDeleteClick(key)}
                      aria-label="Delete key"
                      disabled={deletingKeyId !== null}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )) : <p className="dark:text-text-secondary text-light-text-secondary text-center py-4">No API keys found. Create one to get started.</p>}
          </div>
        )}
      </Card>
      
      {showDeleteConfirm && keyToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <Card title="Delete API Key" className="w-full max-w-md mx-4">
            <p className="text-sm sm:text-base dark:text-text-secondary text-light-text-secondary break-words">
              Are you sure you want to delete the key "{keyToDelete.name}"? This action is permanent and cannot be undone.
            </p>
            <div className="flex flex-col sm:flex-row flex-col sm:flex-row justify-end gap-2 sm:gap-2 sm:gap-4 mt-6">
              <Button variant="secondary" onClick={() => setShowDeleteConfirm(false)} className="w-full sm:w-auto">
                Cancel
              </Button>
              <Button 
                variant="danger" 
                onClick={confirmDelete}
                isLoading={deletingKeyId === keyToDelete.apiKeyId}
                className="w-full sm:w-auto"
              >
                Delete
              </Button>
            </div>
          </Card>
        </div>
      )}
      
      {/* Toast Notification */}
      {showCopiedToast && (
        <div className="fixed bottom-4 right-4 z-50">
          <Toast
            message="Copied to clipboard"
            type="success"
            onClose={() => setShowCopiedToast(false)}
            duration={2000}
          />
        </div>
      )}
    </div>
  );
};

export default ApiKeysPage;